import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Check, Calendar, MapPin, Users2, Eye, Heart,
  MessageSquare, Scissors, Music2, Youtube, Instagram, Twitter,
  Crown, ShieldCheck, TrendingUp, Sparkles, Play,
} from 'lucide-react';
import { layerSeekAssets } from '../../data/layerSeekAssets';

const ZeditProducts = () => {
  return (
    <section id="products" className="py-20 sm:py-28 relative">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-14 sm:mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--accent-primary)]">
            // Product Suite
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
            How we deliver
            <br />
            growth.
          </h2>
          <p className="mt-5 text-base sm:text-lg font-light text-[var(--text-secondary)] leading-relaxed max-w-2xl">
            Here&apos;s how protocols benefit &amp; grow with us.
          </p>
        </div>

        <div className="space-y-5 sm:space-y-6">
          <ProductRow
            slug="layerseek"
            tag="Live Events"
            badge="November 20, 2026"
            name="LayerSeek"
            tagline="Every Deal That Matters in DeFi Starts in This Room"
            description="A virtual summit where DeFi protocols meet the builders and players that help them land grants, ship B2B integrations faster, and close co-marketing and joint venture deals."
            bullets={[
              'Virtual venue with 9 floors and live booths',
              "AI matchmaking based on what you're seeking or offering",
              'Tickets from $297. Sponsorships from $997',
            ]}
            primaryCta={{ label: 'Explore LayerSeek', href: '/layerseek' }}
            secondaryCta={{ label: 'Get tickets', href: '/layerseek#pricing' }}
            thumbnail={<LayerSeekThumbnail />}
          />

          <ProductRow
            slug="clips"
            tag="Short-form Distribution"
            badge="CPM-priced"
            name="Clips"
            tagline="Achieve organic mindshare through strategic clip distribution."
            description="Founder-led short-form content created and distributed where real attention exists. We assist with content creation via creator collabs, editing, and multi-platform distribution."
            bullets={[
              'Creator network of podcasters & AMA hosts',
              'Multi-platform distribution (TikTok, Reels, Shorts, X)',
              'Algorithm-powered reach with real-time analytics',
            ]}
            primaryCta={{ label: 'Explore Clips', href: '/clips' }}
            secondaryCta={{ label: 'Book a strategy call', href: 'mailto:hi@zedit.io?subject=Clips' }}
            thumbnail={<ClipsThumbnail />}
            reverse
          />

          <ProductRow
            slug="truekol"
            tag="KOL Network"
            badge="Invite only · Coming soon"
            name="TrueKOL"
            tagline="Direct access. Real pricing. Zero agency markup."
            description="A vetted directory of 2,500+ crypto KOLs with real engagement data, true collaboration costs, and direct contacts. Skip the agencies, skip the inflated invoices."
            bullets={[
              '2,500+ vetted KOLs across crypto Twitter',
              'Verified true pricing with no markup',
              'Direct contact across YouTube, X, Discord, and Telegram',
            ]}
            primaryCta={{ label: 'See TrueKOL', href: '/truekol' }}
            secondaryCta={{ label: 'Request access', href: '/truekol#access' }}
            thumbnail={<TrueKolThumbnail />}
          />
        </div>
      </div>
    </section>
  );
};

const ProductRow = ({
  slug, tag, badge, name, tagline, description, bullets,
  primaryCta, secondaryCta, thumbnail, reverse,
}) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
      data-testid={`product-section-${slug}`}
      className="rounded-[32px] bg-[var(--bg-secondary)] border border-[var(--border-subtle)] overflow-hidden hover:border-[var(--accent-primary)]/40 transition-colors"
    >
      <div className={`grid lg:grid-cols-12 gap-0 ${reverse ? 'lg:[direction:rtl]' : ''}`}>
        {/* Copy side */}
        <div className="lg:col-span-6 p-8 sm:p-10 lg:p-12 [direction:ltr] flex flex-col justify-center">
          <div className="inline-flex flex-wrap items-center gap-2 mb-5">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-md text-[var(--accent-primary)] bg-[var(--accent-bg)] border border-[var(--accent-primary)]/30">
              {tag}
            </span>
            {badge && (
              <span className="text-[10px] uppercase tracking-widest font-medium text-[var(--text-muted)]">
                · {badge}
              </span>
            )}
          </div>
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] text-white mb-4">
            {name}
          </h3>
          <p className="text-base sm:text-lg font-medium text-white leading-relaxed mb-2">
            {tagline}
          </p>
          <p className="text-sm sm:text-base font-light text-[var(--text-muted)] leading-relaxed mb-6">
            {description}
          </p>
          <ul className="space-y-2.5 mb-8">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm font-medium text-[var(--text-secondary)]">
                <span className="w-5 h-5 rounded-md flex-shrink-0 flex items-center justify-center mt-0.5 bg-[var(--accent-bg)]">
                  <Check className="w-3 h-3 text-[var(--accent-primary)]" />
                </span>
                {b}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-3">
            <Link
              to={primaryCta.href}
              data-testid={`product-cta-${slug}`}
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-[var(--accent-primary)] text-black font-semibold text-sm hover:scale-105 transition-transform"
              style={{ boxShadow: '0 8px 24px -8px rgba(218,255,1,0.45)' }}
            >
              {primaryCta.label}
              <ArrowRight className="w-4 h-4" />
            </Link>
            {secondaryCta && (
              <a
                href={secondaryCta.href}
                data-testid={`product-secondary-cta-${slug}`}
                className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-white font-semibold text-sm hover:border-[var(--accent-primary)] hover:bg-[var(--accent-bg)] transition-all"
              >
                {secondaryCta.label}
              </a>
            )}
          </div>
        </div>

        {/* Thumbnail side */}
        <div className="lg:col-span-6 p-6 sm:p-8 lg:p-10 [direction:ltr] flex items-center justify-center bg-gradient-to-br from-[var(--bg-tertiary)] to-[var(--bg-secondary)] relative overflow-hidden">
          {thumbnail}
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────── THUMBNAILS ─────────────────── */

// LayerSeek · uses the real mainstage GIF inside a clean device frame
const LayerSeekThumbnail = () => {
  const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_defi-protocol-summit/artifacts/gmej8bdg_Event%20experience%204k.mp4';
  const miro = layerSeekAssets.features.find((f) => f.id === 'miro');
  return (
    <div className="w-full max-w-md relative">
      {/* Glow */}
      <div className="absolute inset-0 bg-[var(--accent-primary)] opacity-10 blur-3xl rounded-full" />

      {/* Main browser-frame with 4K event experience video */}
      <div className="relative rounded-2xl bg-[#0a0a0c] border border-[var(--border-subtle)] overflow-hidden shadow-2xl">
        <div className="flex items-center gap-1.5 px-3 py-2 bg-black/40 border-b border-[var(--border-subtle)]">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-yellow-400" />
          <span className="w-2 h-2 rounded-full bg-green-400" />
          <div className="ml-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/5 text-[10px] font-mono text-white/70">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)] animate-pulse" />
            layerseek.zedit.io · LIVE
          </div>
          <div className="ml-auto flex items-center gap-2 text-[10px] text-white/50">
            <Users2 className="w-3 h-3" /> 1,243
          </div>
        </div>
        <div className="aspect-[16/10] relative bg-black">
          <video
            src={VIDEO_URL}
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
          {/* Bottom overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Floor 3 · Networking Lounge
              </span>
              <span className="text-[10px] text-white/70">9 floors live</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating chips */}
      <div className="absolute -top-3 -right-3 px-3 py-2 rounded-xl bg-[var(--accent-primary)] text-black shadow-lg flex items-center gap-1.5 z-10">
        <Calendar className="w-3.5 h-3.5" />
        <span className="text-xs font-bold">Nov 20</span>
      </div>
      <div className="absolute -bottom-4 -left-3 px-3 py-2 rounded-xl bg-[#0a0a0c] border border-[var(--border-subtle)] shadow-lg flex items-center gap-2 z-10">
        <div className="w-8 h-8 rounded-md bg-white/5 overflow-hidden">
          <img src={miro?.image} alt="Miro" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div>
          <div className="text-[10px] font-bold text-white">9 venue floors</div>
          <div className="text-[9px] text-[var(--text-muted)]">Live booths · whiteboards</div>
        </div>
      </div>
    </div>
  );
};

// Clips · polished phone-mockup thumbnail showing vertical video feeds + analytics chip
const SFC_THUMB_1 = 'https://customer-assets.emergentagent.com/job_fb684b19-cb6a-4dd0-9eb3-cce9aac29bc7/artifacts/pjqeebuw_sfc%20thumbnail%201.jpg';
const SFC_THUMB_2 = 'https://customer-assets.emergentagent.com/job_fb684b19-cb6a-4dd0-9eb3-cce9aac29bc7/artifacts/0m95i6ww_sfc%20thumbnail%202.jpg';
const SFC_THUMB_3 = 'https://customer-assets.emergentagent.com/job_fb684b19-cb6a-4dd0-9eb3-cce9aac29bc7/artifacts/a8xjhu97_sfc%20thumbnail%203.jpg';

const ClipsThumbnail = () => (
  <div className="w-full max-w-md relative py-4">
    <div className="absolute inset-0 bg-[var(--accent-primary)] opacity-8 blur-3xl rounded-full" />

    <div className="relative flex items-end justify-center gap-3">
      {/* Phone 1 - tilted left */}
      <div
        className="w-32 sm:w-36 rounded-[20px] bg-black border border-[var(--border-subtle)] overflow-hidden shadow-2xl"
        style={{ transform: 'rotate(-8deg) translateY(8px)' }}
      >
        <div className="aspect-[9/16] bg-black relative">
          <img src={SFC_THUMB_1} alt="Coin Gainz creator clip" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/25" />
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/60 text-[8px] font-bold text-white">
            <Music2 className="w-2 h-2" /> TikTok
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
              <Play className="w-3.5 h-3.5 text-black fill-black ml-0.5" />
            </div>
          </div>
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
            <span className="text-[9px] font-bold text-white flex items-center gap-1"><Eye className="w-2.5 h-2.5" /> 2.1M</span>
            <Heart className="w-3 h-3 text-white" />
          </div>
        </div>
      </div>

      {/* Phone 2 - center (taller) */}
      <div className="w-36 sm:w-40 rounded-[20px] bg-black border border-[var(--accent-primary)]/40 overflow-hidden shadow-2xl relative z-10">
        <div className="aspect-[9/16] bg-black relative">
          <img src={SFC_THUMB_2} alt="Web3 podcast studio clip" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/25" />
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/60 text-[8px] font-bold text-white">
            <Youtube className="w-2 h-2" /> Shorts
          </div>
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--accent-primary)] text-[8px] font-bold text-black">
            TRENDING
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
              <Play className="w-4 h-4 text-black fill-black ml-0.5" />
            </div>
          </div>
          <div className="absolute bottom-2 left-2 right-2">
            <div className="text-[9px] font-bold text-white mb-0.5">Founder explains L2 yield</div>
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-bold text-white flex items-center gap-1"><Eye className="w-2.5 h-2.5" /> 890K</span>
              <span className="text-[9px] font-bold text-white flex items-center gap-1"><MessageSquare className="w-2.5 h-2.5" /> 4.2K</span>
            </div>
          </div>
        </div>
      </div>

      {/* Phone 3 - tilted right */}
      <div
        className="w-32 sm:w-36 rounded-[20px] bg-black border border-[var(--border-subtle)] overflow-hidden shadow-2xl"
        style={{ transform: 'rotate(8deg) translateY(8px)' }}
      >
        <div className="aspect-[9/16] bg-black relative">
          <img src={SFC_THUMB_3} alt="Crypto Daily short clip" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/60 text-[8px] font-bold text-white">
            <Instagram className="w-2 h-2" /> Reels
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
              <Play className="w-3.5 h-3.5 text-black fill-black ml-0.5" />
            </div>
          </div>
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
            <span className="text-[9px] font-bold text-white flex items-center gap-1"><Eye className="w-2.5 h-2.5" /> 1.4M</span>
            <Heart className="w-3 h-3 text-white" />
          </div>
        </div>
      </div>
    </div>

    {/* Floating analytics chip */}
    <div className="absolute -top-2 -right-2 px-3 py-2 rounded-xl bg-[#0a0a0c] border border-[var(--accent-primary)]/30 shadow-lg z-20">
      <div className="text-[9px] uppercase tracking-widest text-[var(--text-muted)]">Total reach</div>
      <div className="text-lg font-black text-[var(--accent-primary)]">12.4M</div>
    </div>
    <div className="absolute -bottom-2 -left-2 px-3 py-2 rounded-xl bg-[#0a0a0c] border border-[var(--border-subtle)] shadow-lg z-20 flex items-center gap-2">
      <Scissors className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
      <span className="text-[10px] font-bold text-white">412 clips live</span>
    </div>
  </div>
);

// TrueKOL · polished KOL directory dashboard thumbnail
const PlatformBadge = ({ p }) => {
  const styles = {
    youtube: { bg: '#FF0000', label: 'YT' },
    x: { bg: '#0f172a', label: 'X' },
    discord: { bg: '#5865F2', label: 'DC' },
    telegram: { bg: '#26A5E4', label: 'TG' },
  };
  const s = styles[p];
  if (!s) return null;
  return (
    <span
      className="inline-flex items-center justify-center w-4 h-4 rounded-[3px] text-[7px] font-black text-white"
      style={{ background: s.bg }}
      title={p}
    >
      {s.label}
    </span>
  );
};

const TrueKolThumbnail = () => {
  const kols = [
    {
      avatar: 'https://i.pravatar.cc/80?img=13',
      name: 'Michael Zhang',
      handle: '@cryptomike',
      following: '328K',
      platforms: ['x', 'youtube', 'telegram'],
      price: '$2,400',
      eng: '4.8%',
      bars: [0.5, 0.7, 0.6, 0.85, 0.7, 0.95],
    },
    {
      avatar: 'https://i.pravatar.cc/80?img=47',
      name: 'Sarah Chen',
      handle: '@sarah.eth',
      following: '184K',
      platforms: ['x', 'discord'],
      price: '$1,200',
      eng: '6.2%',
      bars: [0.6, 0.75, 0.55, 0.8, 0.65, 0.9],
    },
    {
      avatar: 'https://i.pravatar.cc/80?img=68',
      name: 'James Okonkwo',
      handle: '@jamesonchain',
      following: '412K',
      platforms: ['youtube', 'x', 'discord', 'telegram'],
      price: '$3,800',
      eng: '3.4%',
      bars: [0.4, 0.55, 0.45, 0.7, 0.5, 0.75],
    },
  ];
  return (
    <div className="w-full max-w-md relative">
      <div className="absolute inset-0 bg-[var(--accent-primary)] opacity-8 blur-3xl rounded-full" />

      <div className="relative rounded-2xl bg-[#0a0a0c] border border-[var(--border-subtle)] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center justify-between bg-black/40">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[var(--accent-primary)] flex items-center justify-center">
              <Crown className="w-3.5 h-3.5 text-black" />
            </div>
            <div>
              <div className="text-xs font-bold text-white leading-none">TrueKOL</div>
              <div className="text-[9px] text-[var(--text-muted)] leading-none mt-0.5">2,500+ vetted KOLs · Invite only</div>
            </div>
          </div>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[var(--accent-primary)]/15 border border-[var(--accent-primary)]/40 text-[var(--accent-primary)] uppercase tracking-wider">
            Launching soon
          </span>
        </div>

        {/* Filter chips */}
        <div className="px-4 pt-3 flex gap-1.5 overflow-hidden">
          {['DeFi', 'L2s', 'Infra', 'Perps'].map((f, i) => (
            <span
              key={f}
              className={`px-2 py-0.5 rounded-md text-[9px] font-bold ${
                i === 0
                  ? 'bg-[var(--accent-primary)] text-black'
                  : 'bg-white/5 text-white/60 border border-white/10'
              }`}
            >
              {f}
            </span>
          ))}
        </div>

        {/* KOL list */}
        <div className="p-3 space-y-1.5">
          {kols.map((k) => (
            <div
              key={k.handle}
              className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white/[0.03] border border-white/10"
            >
              <div className="relative flex-shrink-0">
                <img
                  src={k.avatar}
                  alt={k.name}
                  className="w-9 h-9 rounded-full object-cover border border-white/10"
                  loading="lazy"
                />
                <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[var(--accent-primary)] border border-[#0a0a0c] flex items-center justify-center">
                  <Check className="w-2 h-2 text-black" strokeWidth={3} />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[11px] font-bold text-white truncate">{k.handle}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  {k.platforms.map((p) => <PlatformBadge key={p} p={p} />)}
                  <span className="text-[9px] text-[var(--text-muted)] font-medium ml-0.5">
                    {k.following} following
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-[8px] text-[var(--text-muted)] uppercase tracking-wider leading-none">True</div>
                <div className="text-xs font-black text-[var(--accent-primary)] leading-none mt-0.5">{k.price}</div>
                <div className="text-[8px] text-emerald-300 leading-none mt-0.5">{k.eng} eng</div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[var(--border-subtle)] flex items-center justify-between text-[10px] bg-black/40">
          <span className="text-[var(--text-muted)]">Direct access · No agency markup</span>
          <span className="text-[var(--accent-primary)] font-bold">Verified pricing ✓</span>
        </div>
      </div>

      {/* Floating chips */}
      <div className="absolute -top-3 -right-3 px-3 py-2 rounded-xl bg-[var(--accent-primary)] text-black shadow-lg flex items-center gap-1.5 z-10">
        <ShieldCheck className="w-3.5 h-3.5" />
        <span className="text-xs font-bold">Vetted</span>
      </div>
      <div className="absolute -bottom-4 -left-3 px-3 py-2 rounded-xl bg-[#0a0a0c] border border-[var(--border-subtle)] shadow-lg flex items-center gap-2 z-10">
        <TrendingUp className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
        <div>
          <div className="text-[10px] font-bold text-white">$2.2K saved / campaign*</div>
          <div className="text-[9px] text-[var(--text-muted)]">*per $10K budget spent</div>
        </div>
      </div>
    </div>
  );
};

export default ZeditProducts;
